import { useEffect, useRef, useState } from 'react';
interface Options {
  sectionIds: string[];
  /** Fired once per section, the first time it becomes the active one. */
  onSectionViewed?: (id: string) => void;
}

/**
 * Tracks which section the reader is currently in.
 *
 * Uses ONE IntersectionObserver, deliberately. Navbar.tsx already installs a
 * window scroll listener; adding a second one that runs layout maths across a
 * seven-section document on every scroll event is exactly the kind of thing
 * that makes a long page feel cheap. The observer also drives the deduped
 * analytics event, so section tracking costs nothing extra.
 *
 * Two edge cases that a naive implementation gets wrong:
 *
 *  - The LAST section may never cross the activation threshold, because the
 *    page bottom arrives first. Handled with an explicit at-bottom check.
 *  - IntersectionObserver may be missing (old browsers, some test envs). The
 *    hook falls back to the first section rather than rendering no active state.
 */
export function useSectionSpy({
  sectionIds,
  onSectionViewed
}: Options) {
  const [activeId, setActiveId] = useState<string>(sectionIds[0] ?? '');
  const viewed = useRef(new Set<string>());
  const viewedCallback = useRef(onSectionViewed);
  viewedCallback.current = onSectionViewed;
  useEffect(() => {
    if (sectionIds.length === 0) return;
    if (typeof IntersectionObserver === 'undefined') {
      setActiveId(sectionIds[0]);
      return;
    }
    const visible = new Map<string, number>();
    const activate = (id: string) => {
      setActiveId(id);
      if (!viewed.current.has(id)) {
        viewed.current.add(id);
        viewedCallback.current?.(id);
      }
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const id = entry.target.id;
        if (entry.isIntersecting) visible.set(id, entry.boundingClientRect.top);else visible.delete(id);
      }
      if (visible.size === 0) return;
      // Topmost visible section wins.
      const [topmost] = [...visible.entries()].sort((a, b) => a[1] - b[1]);
      activate(topmost[0]);
    }, {
      // Top offset clears the 4rem fixed navbar plus breathing room, so a
      // section counts as active once its heading is comfortably on screen.
      rootMargin: '-112px 0px -55% 0px',
      threshold: 0
    });
    const elements = sectionIds.map(id => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);
    elements.forEach(el => observer.observe(el));

    // Page-bottom rule: the final section often never reaches the activation
    // band, so pin it explicitly once the reader hits the end.
    const onScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 80;
      if (atBottom) activate(sectionIds[sectionIds.length - 1]);
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [sectionIds]);
  return activeId;
}